import { useQuery, useMutation } from "@tanstack/react-query";
import apiClient, { ApiResponse } from "./apiClient";

interface PresignedUrlResponse {
  url: string;
  key: string;
  fields: Record<string, string>;
  max_size_bytes: number;
  expires_in: number;
}

interface ImageUploadPayload {
  filename: string;
  content_type: string;
}

const QUERY_KEY = ["images"];

const fetchImageUrl = async (id: string): Promise<string> => {
  const { data: response } = await apiClient.get<ApiResponse<{ url: string }>>(`/v1/admin/media/download-url/${id}`);
  return response.data.url;
};

const uploadImage = async ({ file, filename, content_type }: ImageUploadPayload & { file: File }): Promise<{ url: string; key: string }> => {
  // Step 1: get pre-signed S3 POST data from our API
  const { data: response } = await apiClient.post<ApiResponse<PresignedUrlResponse>>(
    "/v1/admin/media/upload-url",
    { filename, content_type }
  );

  const { url, key, fields } = response.data;

  // Step 2: POST to S3 as multipart/form-data with all pre-signed fields, file appended last
  const formData = new FormData();
  Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
  formData.append("file", file);

  const s3Response = await fetch(url, { method: "POST", body: formData });
  if (!s3Response.ok) {
    throw new Error("Image upload failed. Please try again.");
  }

  return { url, key };
};

export const useImageUrl = (id: string) =>
  useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => fetchImageUrl(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

export const useUploadImage = () =>
  useMutation({
    mutationFn: uploadImage,
  });
